import { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { AnalyticsPeriod, MainCategory } from '../types';
import { formatCurrency, formatDateKey } from '../utils/format';
import { LEGACY_TO_PRIME, PRIME_CATEGORIES } from '../utils/categorization';

interface Props {
  periods: AnalyticsPeriod[];
  mainCategories?: MainCategory[];
  groupBy?: 'overview' | 'main' | 'category';
}

const PRIME_COLOR: Record<string, string> = {};
for (const p of PRIME_CATEGORIES) PRIME_COLOR[p.id] = p.color;

/** Distinct color palette for individual categories in detailed view */
const CATEGORY_PALETTE = [
  '#f43f5e', '#fb923c', '#fbbf24', '#a3e635', '#34d399',
  '#22d3ee', '#60a5fa', '#a78bfa', '#f472b6', '#e879f9',
  '#fb7185', '#38bdf8', '#818cf8', '#4ade80', '#facc15',
  '#f97316', '#06b6d4', '#8b5cf6', '#ec4899', '#14b8a6',
  '#ef4444', '#0ea5e9', '#6366f1', '#10b981', '#eab308',
];

function hashString(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

const _categoryColorCache: Record<string, string> = {};
function categoryColor(catName: string): string {
  if (_categoryColorCache[catName]) return _categoryColorCache[catName];
  const idx = hashString(catName) % CATEGORY_PALETTE.length;
  const color = CATEGORY_PALETTE[idx];
  _categoryColorCache[catName] = color;
  return color;
}

export function TrendLineChart({
  periods,
  mainCategories = [],
  groupBy = 'overview',
}: Props) {
  const data = useMemo(() => {
    if (groupBy === 'overview') {
      return periods.map(p => ({
        name: formatDateKey(p.date_key),
        Income: Math.round(p.total_income * 100) / 100,
        Spent: Math.round(p.total_spent * 100) / 100,
        Savings: Math.round((p.total_income - p.total_spent) * 100) / 100,
      }));
    }

    if (groupBy === 'main' && mainCategories.length > 0) {
      return periods.map(period => {
        const row: Record<string, string | number> = { name: formatDateKey(period.date_key) };

        // Aggregate flat categories → prime totals
        const primeTotals: Record<string, number> = {};
        for (const [cat, amount] of Object.entries(period.categories)) {
          const primeId = (LEGACY_TO_PRIME[cat] ?? 'others').toLowerCase();
          primeTotals[primeId] = (primeTotals[primeId] ?? 0) + amount;
        }

        // Always write all 5 prime categories (even if 0)
        for (const mc of mainCategories) {
          row[mc.name] = Math.round((primeTotals[mc.id] ?? 0) * 100) / 100;
        }
        return row;
      });
    }

    // Default: category breakdown
    const allCategories = Array.from(new Set(periods.flatMap(p => Object.keys(p.categories))));
    return periods.map(period => {
      const row: Record<string, string | number> = { name: formatDateKey(period.date_key) };
      allCategories.forEach(cat => {
        row[cat] = Math.round((period.categories[cat] ?? 0) * 100) / 100;
      });
      return row;
    });
  }, [periods, mainCategories, groupBy]);

  const linesToShow = useMemo(() => {
    if (groupBy === 'overview') {
      return [
        { key: 'Income',  color: '#10b981', name: 'Income' },
        { key: 'Spent',   color: '#f43f5e', name: 'Spent' },
        { key: 'Savings', color: '#06b6d4', name: 'Savings' },
      ];
    }

    if (groupBy === 'main' && mainCategories.length > 0) {
      return mainCategories.map(mc => ({
        key: mc.name,
        color: PRIME_COLOR[mc.id] ?? '#52525b',
        name: mc.name,
      }));
    }

    const allCategories = Array.from(new Set(periods.flatMap(p => Object.keys(p.categories))));
    return allCategories.map(cat => ({
      key: cat,
      color: categoryColor(cat),
      name: cat,
    }));
  }, [mainCategories, groupBy, periods]);

  if (data.length === 0) {
    return <EmptyChart />;
  }

  const isMainView = groupBy === 'main' && mainCategories.length > 0;

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" vertical={false} />
        <XAxis
          dataKey="name"
          tick={{ fill: '#71717a', fontSize: 11 }}
          axisLine={{ stroke: '#27272a' }}
          tickLine={false}
        />
        <YAxis
          tick={{ fill: '#71717a', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          width={64}
          tickFormatter={(v: number) => formatCurrency(v)}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: '#0a0a0a',
            border: '1px solid #27272a',
            borderRadius: 10,
            fontSize: 12,
          }}
          formatter={(value: unknown, name: unknown) => [
            formatCurrency(Number(value)),
            String(name),
          ]}
        />
        <Legend
          wrapperStyle={{ fontSize: 11, color: '#a1a1aa' }}
          iconType="line"
          iconSize={16}
        />
        {linesToShow.map(line => (
          <Line
            key={line.key}
            type="monotone"
            dataKey={line.key}
            stroke={line.color}
            strokeWidth={isMainView ? 2.5 : 2}
            dot={{ r: isMainView ? 4 : 3, fill: line.color, strokeWidth: 0 }}
            activeDot={{ r: 6, strokeWidth: 0 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function EmptyChart() {
  return (
    <div className="flex h-[300px] items-center justify-center text-sm text-zinc-600">
      No data available for the selected timeframe.
    </div>
  );
}
